import {Alert, Backdrop, Box, CircularProgress, useTheme} from '@mui/material';
import dayjs from 'dayjs';
import {t} from 'i18next';
import update from 'immutability-helper';
import * as _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {MAX_BAR_SIZE} from '../../../constants/common';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  HazardAIDetectionChartQuery,
  HazardAIDetectionChartResponse,
} from '../../../interfaces/HazardAIDetectionChart';
import reduxSelectors from '../../../redux/selectors';
import SpeedSliderSelector from '../../common/SpeedSliderSelector';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {SafeyeNodeSelector} from '../../selectors/SafeyeNodeSelector';
import {SitesSelect} from '../../selectors/SitesSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';
import HazardAISummaryGrid from '../HazardAIMachineUtilizationChart/HazardAISummaryGrid';
import {ObjectTypeSelector} from './ObjectTypeSelector';

const DEFAULT_SHOWN_FIELDS = [
  'safeye_node_id',
  'machine_name',
  'hazard_person',
  'warning_person',
  'id_person',
  'hazard_vehicle',
  'warning_vehicle',
  'id_vehicle',
  'hazard_object',
  'warning_object',
  'id_object',
];

enum ObjectType {
  all = 'all',
  person = 'person',
  vehicle = 'vehicle',
  vision_impaired = 'vision impaired',
}

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value?: DashboardPanelData) => void;
}

export const HazardAISummaryChart = (props?: Props) => {
  const isDarkMode = useSelector(reduxSelectors.app.getIsDarkMode);
  const theme = useTheme();
  const [panel] = usePanel();
  const {filter, setFilter} = usePanelFilter();

  const [fetchedData, setFetchedData] =
    useState<HazardAIDetectionChartResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const [objectType, setObjectType] = useState<ObjectType>(
    props?.value?.object_type ?? ObjectType.all
  );

  const params = useMemo<HazardAIDetectionChartQuery>(
    () => ({
      date_start:
        filter.params?.date_start ??
        props?.value?.params?.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        filter.params?.date_end ??
        props?.value?.params?.date_end ??
        dayjs().format('YYYY-MM-DD'),
      safeye_node_id: _.has(filter.params, 'safeye_node_ids')
        ? filter.params?.safeye_node_ids
        : props?.value?.params?.safeye_node_id,
      site_id: _.has(filter.params, 'site_ids')
        ? filter.params?.site_ids
        : props?.value?.params?.site_id,
      speed_min: filter.params?.speed_min || 0,
      speed_max: filter.params?.speed_max || 200,
    }),
    [filter.params]
  );

  const data = useMemo(() => {
    props?.onUpdate?.({...props.value, object_type: objectType});
    if (fetchedData) {
      if (objectType === 'all') {
        const returnArray: any[] = [];
        const typeOptions = ['person', 'vehicle', 'object'];

        typeOptions.forEach((d) => {
          returnArray.push({
            name: d,
            //@ts-ignore
            ...fetchedData[d],
          });
        });
        return returnArray;
      } else {
        return [fetchedData[objectType]];
      }
    }
  }, [fetchedData, objectType]);

  const lastPersisted = useRef<unknown>(props?.value?.params);
  useEffect(() => {
    if (params) {
      fetchData(params);
      if (!_.isEqual(params, lastPersisted.current)) {
        lastPersisted.current = params;
        props?.onUpdate?.({...props.value, params});
      }
    }
  }, [params]);

  const fetchData = useCallback(
    async (params: HazardAIDetectionChartQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/hazard-ai/detection-chart`;
        const resp = await API.get<HazardAIDetectionChartResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_1e552cd4');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 38px)"
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      {/* Filters */}
      <Box display="flex" gap={1} py={1}>
        <Box minWidth={200}>
          <ObjectTypeSelector
            value={objectType}
            size="small"
            fullWidth
            onChange={(v) => setObjectType(v as ObjectType)}
          />
        </Box>
        <Box maxWidth={400} minWidth={200}>
          <SitesSelect
            size="small"
            multiple
            fullWidth
            value={params.site_id}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    site_ids: {
                      $set: v as number[],
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400} minWidth={200}>
          <SafeyeNodeSelector
            size="small"
            multiple
            fullWidth
            value={params.safeye_node_id as unknown as number[]}
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    safeye_node_ids: {
                      $set: v as unknown as string[],
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box maxWidth={400}>
          <DateRangeSelect
            value={[
              dayjs(params.date_start).toDate(),
              dayjs(params.date_end).toDate(),
            ]}
            size="small"
            onChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    date_start: {
                      $set: v?.[0]
                        ? dayjs(v?.[0]).format('YYYY-MM-DD')
                        : undefined,
                    },
                    date_end: {
                      $set: v?.[1]
                        ? dayjs(v?.[1]).format('YYYY-MM-DD')
                        : undefined,
                    },
                  },
                })
              );
            }}
          />
        </Box>
        <Box minWidth={400} display="flex" alignItems="center" columnGap="14px">
          <SpeedSliderSelector
            min={0}
            max={200}
            lowerValue={Number(params.speed_min)}
            upperValue={Number(params.speed_max)}
            onLowerChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    speed_min: {
                      $set: v as number,
                    },
                  },
                })
              );
            }}
            onUpperChange={(v) => {
              setFilter(
                update(filter, {
                  params: {
                    speed_max: {
                      $set: v as number,
                    },
                  },
                })
              );
            }}
          ></SpeedSliderSelector>
        </Box>
      </Box>

      {/* Errors */}
      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <Box
        display="flex"
        flexDirection="column"
        height="calc(100% - 56px)"
        gap={1}
      >
        {/* Chart */}
        <Box
          width="100%"
          height="50%"
          bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
        >
          {!fetchedInProgress && !!data && (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                maxBarSize={MAX_BAR_SIZE}
                margin={{
                  top: 30,
                  bottom: 10,
                  right: 10,
                }}
              >
                <XAxis
                  dataKey={(d) =>
                    d?.name?.toUpperCase() || objectType.toUpperCase()
                  }
                />
                <YAxis />
                <TooltipRecharts
                  shared={false}
                  labelStyle={{
                    color: theme.palette.text.secondary,
                  }}
                  contentStyle={{
                    backgroundColor: theme.palette.background.default,
                  }}
                  cursor={false}
                />
                <Legend />
                <Bar
                  dataKey="warning"
                  name="Warning"
                  label={{
                    position: 'top',
                    fill: theme.palette.text.secondary,
                  }}
                  fill="yellow"
                />
                <Bar
                  dataKey="hazard"
                  name="Hazard"
                  label={{
                    position: 'top',
                    fill: theme.palette.text.secondary,
                  }}
                  fill="red"
                />
                <Bar
                  dataKey="id"
                  name="Id"
                  label={{
                    position: 'top',
                    fill: theme.palette.text.secondary,
                  }}
                  fill="purple"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
          {(fetchedInProgress || !data) && (
            <Box
              display="flex"
              height="100%"
              alignItems="center"
              justifyContent="center"
            >
              {fetchedInProgress ? 'Loading...' : 'No data to display'}
            </Box>
          )}
        </Box>

        {/* Grid */}
        <HazardAISummaryGrid
          value={{
            ...props?.value,
            shownFields: props?.value?.shownFields || DEFAULT_SHOWN_FIELDS,
            columnWidths: props?.value?.columnWidths,
            columnOrder: props?.value?.columnOrder,
            selectedIds: props?.value?.selectedIds,
            grid: props?.value?.grid,
            params: props?.value?.params,
          }}
          onChange={(v) => {
            props?.onUpdate?.({
              ...props.value,
              ...v,
            });
          }}
        />
      </Box>
    </Box>
  );
};
